import React, { useState, useEffect } from "react";
import { api } from "../../utils/api";
import { useParams } from "react-router-dom";
import { Button, Tabs, Tab, Box, Chip } from "@mui/material";
import ModalPopup from "../../components/ModalPopup";
import CustomerForm from "./Forms/CustomerForm";
import ActivateCustomerForm from "./Forms/ActivateCustomerForm";
import AddVehicleToCustomerForm from "./Forms/AddVehicleToCustomerForm";
import { useNavigate } from "react-router-dom";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { COLORS } from "../../utils/colors";
import BasicUserInfo from "../../components/BasicUserInfo";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RedemptionHistory from "./RedemptionHistory";
import AdminContractViewer from "../../components/AdminContractViewer";
import CustomerSubscriptionView from "./CustomerSubscriptionView";
import CustomerCommunications from "./CustomerCommunications";
import AdminCustomerSubscriptions from "./AdminCustomerSubscriptions";
import { QRCodeCanvas } from "qrcode.react";
import { showSuccessToast, showErrorToast } from "../../utils/toast";
import { isHiddenFromCustomerProduct } from "../../utils/hiddenFromCustomerProduct";

const CONTRACT_STATUS_CHIPS = {
  pending: { label: "Pending", color: "warning" },
  accepted: { label: "Signed", color: "success" },
  rejected: { label: "Rejected", color: "error" },
  archived: { label: "Archived", color: "default" },
  expired: { label: "Expired", color: "default" }
};

function getLatestContractForVehicle(contractsByVehicleId, vehicleId) {
  const contracts = contractsByVehicleId[String(vehicleId)] || [];
  return contracts[0] || null;
}

function subscriptionStatusLabel(vehicle) {
  const subs = (vehicle?.activeSubscriptions || []).filter((sub) => sub && sub.deleted !== "yes");
  if (!subs.length) return "No membership";
  if (subs.some((sub) => sub.active)) return "Active";
  return "Inactive";
}

const CustomerSingle = () => {
  let navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [editCustomerState, setEditCustomerState] = useState(false);

  // const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [deleteConfirmationIsOpen, setDeleteConfirmationIsOpen] = useState(false);
  const [showContractViewer, setShowContractViewer] = useState(false);
  const [selectedVehicleForContract, setSelectedVehicleForContract] = useState(null);
  const [showActivateForm, setShowActivateForm] = useState(false);
  const [adminViewTab, setAdminViewTab] = useState(0);
  const [adminSectionTab, setAdminSectionTab] = useState(0);
  const [showIncompleteAccountAlert, setShowIncompleteAccountAlert] = useState(false);
  const [incompleteAccountDetails, setIncompleteAccountDetails] = useState({
    missingVehicle: false,
    missingSubscription: false,
    missingTransaction: false,
    signupLink: null,
    generatingLink: false
  });
  const [showSignupLinkModal, setShowSignupLinkModal] = useState(false);
  const [signupLinkData, setSignupLinkData] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [showProductSelectModal, setShowProductSelectModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedRrp, setSelectedRrp] = useState("");
  const [sendingLink, setSendingLink] = useState({ email: false, sms: false });
  const [addVehicleSubscriptionPreview, setAddVehicleSubscriptionPreview] = useState(null);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [vehicleIdsWithContracts, setVehicleIdsWithContracts] = useState(new Set());
  const [contractsByVehicleId, setContractsByVehicleId] = useState({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchHydratedCustomer = async (customerId) => {
    const customerResponse = await api("v1/customer/" + customerId, "GET", null);
    if (!customerResponse.data.success) return null;

    const hydratedCustomer = customerResponse.data.data.customer;
    let vehicleRefs = Array.isArray(hydratedCustomer.vehicles) ? hydratedCustomer.vehicles : [];

    // Source of truth for vehicles is vehicle.customer; customer.vehicles can occasionally be stale.
    try {
      const customerVehiclesResponse = await api(`v1/vehicle/customer/${customerId}`, "GET", null);
      if (customerVehiclesResponse.data.success && Array.isArray(customerVehiclesResponse.data.data?.allVehicles)) {
        const resolvedVehicleIds = customerVehiclesResponse.data.data.allVehicles
          .map((v) => (typeof v === "string" ? v : v?._id))
          .filter(Boolean);
        if (resolvedVehicleIds.length > 0) {
          vehicleRefs = resolvedVehicleIds;
        }
      }
    } catch (err) {
      console.warn("Failed to fetch vehicles by customer reference, falling back to customer.vehicles", err);
    }

    const updatedVehicleInfo = await Promise.all(
      vehicleRefs.map(async (vehicleRef) => {
        const vehicleId = typeof vehicleRef === "string" ? vehicleRef : vehicleRef?._id;
        if (!vehicleId) return null;
        const fetchVehicleInfo = await api("v1/vehicle/" + vehicleId, "GET");
        let vehicleData = null;
        if (fetchVehicleInfo.data.success) {
          vehicleData = fetchVehicleInfo.data.data.vehicle;
          const isAdmin = sessionStorage.getItem("selectedPortal") === "Administrator";
          const activeSubscriptions = await api(`v1/subscription/search`, "POST", {
            query: { vehicle: vehicleId },
            ...(isAdmin && { adminView: true })
          });
          if (activeSubscriptions.data.success) {
            vehicleData.activeSubscriptions = activeSubscriptions.data.data.searchResult;
          } else {
            vehicleData.activeSubscriptions = null;
          }
        } else {
          vehicleData = { _id: vehicleId };
        }
        return vehicleData;
      })
    );

    hydratedCustomer.vehicles = updatedVehicleInfo.filter(Boolean);
    return hydratedCustomer;
  };

  useEffect(() => {
    //    const fetchCompanyDetails = async () => {
    //     try {
    //       const response = await api('v1/partner/company/'+params.idPartnerCompany, 'GET', null);
    //       if (response.data.success) {
    //         // console.log(response.data.data.partnerCompany);
    //         setSelectedCompany(response.data.data.partnerCompany);
    //         setLoading(false);
    //       }
    //     } catch (error) {
    //       console.error('API error:', error);
    //     }
    //   };

    // fetchCompanyDetails();

    const fetchCustomerDetails = async () => {
      try {
        const hydratedCustomer = await fetchHydratedCustomer(params.idCustomer);
        if (hydratedCustomer) {
          setSelectedCustomer(hydratedCustomer);

          // Fetch contracts for this customer - used to show Contract Information even when subscription search returns empty
          try {
            const contractsRes = await api(`v1/contract/customer/${params.idCustomer}?limit=100`, "GET", null);
            if (contractsRes.data.success && contractsRes.data.data?.contracts?.length > 0) {
              const grouped = {};
              for (const contract of contractsRes.data.data.contracts) {
                const vehicleId = String(contract.vehicle?._id || contract.vehicle || "");
                if (!vehicleId) continue;
                if (!grouped[vehicleId]) grouped[vehicleId] = [];
                grouped[vehicleId].push(contract);
              }
              setContractsByVehicleId(grouped);
              setVehicleIdsWithContracts(new Set(Object.keys(grouped)));
            } else {
              setContractsByVehicleId({});
              setVehicleIdsWithContracts(new Set());
            }
          } catch {
            setContractsByVehicleId({});
            setVehicleIdsWithContracts(new Set());
          }

          // Check if customer account is incomplete
          await checkIncompleteAccount(hydratedCustomer);

          setLoading(false);
        }
      } catch (error) {
        console.error("API error:", error);
      }
    };

    const checkIncompleteAccount = async (customer) => {
      try {
        // Check for vehicles
        const hasVehicles = customer.vehicles && customer.vehicles.length > 0;

        // Check for active subscriptions
        let hasActiveSubscriptions = false;
        if (hasVehicles) {
          for (const vehicle of customer.vehicles) {
            if (vehicle.activeSubscriptions && vehicle.activeSubscriptions.length > 0) {
              hasActiveSubscriptions = true;
              break;
            }
          }
        }

        // Check for transactions
        let hasTransactions = false;
        try {
          const transactionsResponse = await api(`v1/transaction?customer=${customer._id}`, "GET");
          hasTransactions =
            transactionsResponse.data.success &&
            transactionsResponse.data.data &&
            transactionsResponse.data.data.allTransactions &&
            transactionsResponse.data.data.allTransactions.length > 0;
        } catch (error) {
          console.error("Error checking transactions:", error);
          // If transaction check fails, assume no transactions
          hasTransactions = false;
        }

        // Determine if account is incomplete
        const missingVehicle = !hasVehicles;
        const missingSubscription = !hasActiveSubscriptions;
        const missingTransaction = !hasTransactions;

        const isIncomplete = missingVehicle || missingSubscription || missingTransaction;

        setIncompleteAccountDetails({
          missingVehicle,
          missingSubscription,
          missingTransaction
        });

        setShowIncompleteAccountAlert(isIncomplete);

        console.log("Account completeness check:", {
          missingVehicle,
          missingSubscription,
          missingTransaction,
          isIncomplete
        });
      } catch (error) {
        console.error("Error checking account completeness:", error);
      }
    };

    fetchCustomerDetails();
  }, [refreshTrigger, params.idCustomer]);

  const [addVehicleModalLoading, setAddVehicleModalLoading] = useState(false);

  const handleOpenAddVehicleModal = async () => {
    setAddVehicleSubscriptionPreview(null);
    setAddVehicleModalLoading(true);
    try {
      const canAddResponse = await api(`v1/customer/${params.idCustomer}/can-add-vehicle`, "GET");
      if (canAddResponse.data.success && canAddResponse.data.data?.subscriptionPreview) {
        setAddVehicleSubscriptionPreview(canAddResponse.data.data.subscriptionPreview);
      } else {
        setAddVehicleSubscriptionPreview(null);
      }
    } catch (err) {
      console.error("Error fetching subscription preview for add vehicle:", err);
      setAddVehicleSubscriptionPreview(null);
    } finally {
      setAddVehicleModalLoading(false);
    }
    setShowAddVehicleModal(true);
  };

  const handleGenerateSignupLink = async (productId, rrp) => {
    if (!selectedCustomer || !selectedCustomer._id) {
      alert("Customer information is not available. Please refresh the page.");
      return;
    }

    setGeneratingLink(true);

    try {
      // Call backend endpoint to generate signup link
      const response = await api(`v1/customer/${selectedCustomer._id}/generate-signup-link`, "POST", {
        productId: productId,
        rrp: rrp, // Include RRP in the request
        email: selectedCustomer.email,
        firstName: selectedCustomer.firstName,
        lastName: selectedCustomer.lastName,
        phone: selectedCustomer.phone,
        customerId: selectedCustomer._id // Include customer ID to ensure purchase is linked
      });

      if (response.data.success) {
        return {
          success: true,
          signupLink: response.data.data.signupLink,
          signupLinkId: response.data.data.signupLinkId,
          productId: response.data.data.productId
        };
      } else {
        throw new Error(response.data.message || "Unknown error");
      }
    } catch (error) {
      console.error("Error generating signup link:", error);
      throw error;
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleGenerateAndShowSignupLink = async () => {
    if (!selectedCustomer) return;

    try {
      // Fetch available products to show in a selection modal
      const productsResponse = await api("v1/product", "GET");

      if (
        !productsResponse.data.success ||
        !productsResponse.data.data.allProducts ||
        productsResponse.data.data.allProducts.length === 0
      ) {
        alert("No products available. Please add a product first.");
        return;
      }

      // Show product selection modal
      setAvailableProducts(productsResponse.data.data.allProducts);
      setSelectedProductId(null);
      setSelectedRrp("");
      setShowProductSelectModal(true);
    } catch (error) {
      alert(`Failed to fetch products: ${error.message || "Unknown error"}`);
    }
  };

  const handleProductSelected = async () => {
    if (!selectedProductId) {
      alert("Please select a product");
      return;
    }

    const selectedProduct = availableProducts.find((p) => p._id === selectedProductId);
    if (!selectedProduct) {
      alert("Selected product not found");
      return;
    }

    // Validate RRP
    if (!selectedRrp || parseFloat(selectedRrp) <= 0) {
      alert("Please enter a valid RRP price");
      return;
    }

    // Close product selection modal
    setShowProductSelectModal(false);
    setGeneratingLink(true);

    try {
      const result = await handleGenerateSignupLink(selectedProductId, parseFloat(selectedRrp));

      if (result.success) {
        setSignupLinkData({
          signupLink: result.signupLink,
          signupLinkId: result.signupLinkId,
          productId: result.productId,
          productName: selectedProduct.name,
          rrp: parseFloat(selectedRrp),
          customerName: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
          customerEmail: selectedCustomer.email,
          customerPhone: selectedCustomer.phone
        });
        setShowSignupLinkModal(true);
      }
    } catch (error) {
      alert(`Failed to generate signup link: ${error.message || "Unknown error"}`);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleSendViaEmail = async () => {
    if (!signupLinkData || !signupLinkData.signupLinkId || !signupLinkData.customerEmail) {
      showErrorToast("Missing required information to send email.");
      return;
    }

    setSendingLink((prev) => ({ ...prev, email: true }));

    try {
      const response = await api("v1/selfsignup/send", "POST", {
        idSelfSignupLink: signupLinkData.signupLinkId,
        channel: "email",
        email: signupLinkData.customerEmail
      });

      if (response.data.success) {
        showSuccessToast("Signup link sent successfully via email!");
      } else {
        showErrorToast(response.data.message || "Failed to send email.");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      showErrorToast(error.response?.data?.message || "Failed to send email. Please try again.");
    } finally {
      setSendingLink((prev) => ({ ...prev, email: false }));
    }
  };

  const handleSendViaSMS = async () => {
    if (!signupLinkData || !signupLinkData.signupLinkId || !signupLinkData.customerPhone) {
      showErrorToast("Missing required information to send SMS. Customer phone number is required.");
      return;
    }

    setSendingLink((prev) => ({ ...prev, sms: true }));

    try {
      const response = await api("v1/selfsignup/send", "POST", {
        idSelfSignupLink: signupLinkData.signupLinkId,
        channel: "sms",
        phone: signupLinkData.customerPhone
      });

      if (response.data.success) {
        showSuccessToast("Signup link sent successfully via SMS!");
      } else {
        showErrorToast(response.data.message || "Failed to send SMS.");
      }
    } catch (error) {
      console.error("Error sending SMS:", error);
      showErrorToast(error.response?.data?.message || "Failed to send SMS. Please try again.");
    } finally {
      setSendingLink((prev) => ({ ...prev, sms: false }));
    }
  };

  const handleDeleteCustomerConfirm = async () => {
    setLoading(true);
    try {
      const response = await api("v1/customer/" + selectedCustomer._id, "DELETE", null);
      if (response.data.success) {
        navigate("/customer");
      }
    } catch (error) {
      console.error("API error:", error);
    }
    toggleDisplayDeleteConfirm();
    setLoading(false); //once api call is finished.
  };

  const toggleDisplayDeleteConfirm = () => setDeleteConfirmationIsOpen((prev) => !prev);

  const editCustomer = () => {
    setEditCustomerState((prevValue) => !prevValue);
    // setOpenModal(true);
  };

  const handleViewContract = (vehicle) => {
    setSelectedVehicleForContract(vehicle);
    setShowContractViewer(true);
  };

  const handleCloseContractViewer = () => {
    setShowContractViewer(false);
    setSelectedVehicleForContract(null);
  };

  const reloadCustomerDetail = async (updateCustomerDetail) => {
    setEditCustomerState((prevValue) => !prevValue);

    const updatedVehicleInfo = await Promise.all(
      updateCustomerDetail.vehicles.map(async (vehicle) => {
        const fetchVehicleInfo = await api("v1/vehicle/" + vehicle, "GET");
        let vehicleData = null;
        if (fetchVehicleInfo.data.success) {
          vehicleData = fetchVehicleInfo.data.data.vehicle;
        }
        return vehicleData;
      })
    );
    updateCustomerDetail.vehicles = updatedVehicleInfo;
    setSelectedCustomer(updateCustomerDetail);
  };

  console.log("editCustomerState", editCustomerState);
  console.log(selectedCustomer);
  return (
    selectedCustomer && (
      <div className="page partner-customer-single flex flex-column items-center w-full min-h-h-screen p-4 gap-8">
        {loading && <GlobalLoadingSpinner />}

        <ModalPopup
          showModal={editCustomerState}
          handleShowModal={editCustomer}
          popupTitle={"Edit Customer"}
          popupContent={<CustomerForm customer={selectedCustomer} callback={reloadCustomerDetail} />}
          cancelButtonText=""
          confirmButtonText=""
          confirmCallbackAction=""
        />

        <ModalPopup
          showModal={showAddVehicleModal}
          handleShowModal={() => setShowAddVehicleModal(false)}
          popupTitle={"Add Vehicle to Customer"}
          popupContent={
            <AddVehicleToCustomerForm
              customer={selectedCustomer}
              subscriptionPreview={addVehicleSubscriptionPreview}
              callback={async () => {
                setLoading(true);
                try {
                  const hydratedCustomer = await fetchHydratedCustomer(params.idCustomer);
                  if (hydratedCustomer) setSelectedCustomer(hydratedCustomer);
                } catch (err) {
                  console.error("Error reloading customer:", err);
                } finally {
                  setLoading(false);
                }
              }}
              closeModal={() => setShowAddVehicleModal(false)}
            />
          }
          cancelButtonText=""
          confirmButtonText=""
          confirmCallbackAction=""
        />

        <ModalPopup
          showModal={showActivateForm}
          handleShowModal={() => setShowActivateForm(false)}
          popupTitle={"Activate Customer"}
          popupContent={
            <ActivateCustomerForm
              customer={selectedCustomer}
              callback={async () => {
                // Reload customer details after activation
                setLoading(true);
                try {
                  const hydratedCustomer = await fetchHydratedCustomer(params.idCustomer);
                  if (hydratedCustomer) setSelectedCustomer(hydratedCustomer);
                } catch (error) {
                  console.error("Error reloading customer:", error);
                } finally {
                  setLoading(false);
                }
              }}
              closeModal={() => setShowActivateForm(false)}
            />
          }
          cancelButtonText=""
          confirmButtonText=""
          confirmCallbackAction=""
        />

        <div className="pageTitle flex flex-col text-center sm:w-full sm:flex-row sm:justify-between">
          <h1>
            Customer | {selectedCustomer.firstName} {selectedCustomer.lastName}
          </h1>
          <div>
            <IconButton onClick={() => navigate(-1)} sx={{ border: "1px solid grey" }}>
              <ArrowBackIcon />
            </IconButton>
          </div>
        </div>

        {selectedCustomer && (
          <div className="w-full max-w-6xl mx-auto flex flex-col gap-6">
            {sessionStorage.getItem("selectedPortal") == "Administrator" && (
              <Box sx={{ width: "100%" }}>
                <Tabs
                  value={adminViewTab}
                  onChange={(e, newValue) => setAdminViewTab(newValue)}
                  aria-label="admin view tabs"
                >
                  <Tab label="Admin View" />
                  <Tab label="Customer Subscription View" />
                </Tabs>
              </Box>
            )}

            {adminViewTab === 0 && (
              <>
              {sessionStorage.getItem("selectedPortal") === "Administrator" && (
                <Box sx={{ width: "100%", borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={adminSectionTab}
                    onChange={(e, newValue) => setAdminSectionTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="customer admin section tabs"
                  >
                    <Tab label="Customer Profile" />
                    <Tab label="Vehicles" />
                    <Tab label="Subscriptions" />
                    <Tab label="Contracts" />
                    <Tab label="Redemption History" />
                    <Tab label="SMS & Emails" />
                  </Tabs>
                </Box>
              )}

              {sessionStorage.getItem("selectedPortal") === "Administrator" && adminSectionTab === 0 && (
                <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 bg-white">
                    <h2 className="text-lg font-semibold mb-4">Customer Profile</h2>
                    <BasicUserInfo user={selectedCustomer} />
                  </div>
                  <div className="p-4 flex flex-wrap gap-3 bg-gray-50/50">
                    <Button sx={{ backgroundColor: COLORS.priorityBlue.hex }} variant="contained" onClick={editCustomer}>
                      Edit Customer Profile
                    </Button>
                    {!selectedCustomer.active && (
                      <Button sx={{ backgroundColor: COLORS.priorityBlue.hex }} variant="contained" onClick={() => setShowActivateForm(true)}>
                        Activate Customer
                      </Button>
                    )}
                    <Button
                      sx={{ backgroundColor: COLORS.priorityOrange?.hex || "#FF9800" }}
                      variant="contained"
                      onClick={handleGenerateAndShowSignupLink}
                      disabled={generatingLink}
                    >
                      {generatingLink ? "Generating..." : "Generate Signup Link"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        navigate(
                          `/customer/find-merge?q=${encodeURIComponent(
                            selectedCustomer.email || ""
                          )}&target=${selectedCustomer._id}`
                        )
                      }
                    >
                      Find & Merge
                    </Button>
                    <Button className="bg-red-600" variant="contained" color="error" onClick={() => toggleDisplayDeleteConfirm()}>
                      Delete Customer
                    </Button>
                  </div>
                </div>
              )}

              {sessionStorage.getItem("selectedPortal") === "Administrator" && adminSectionTab === 1 && (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Vehicles</h3>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                      onClick={handleOpenAddVehicleModal}
                      disabled={addVehicleModalLoading}
                    >
                      {addVehicleModalLoading ? "Loading..." : "Add Vehicle"}
                    </Button>
                  </div>
                  {selectedCustomer.vehicles && selectedCustomer.vehicles.length > 0 ? (
                    <div className="space-y-3">
                      {selectedCustomer.vehicles.map((vehicle) => {
                        if (!vehicle || !vehicle._id) return null;
                        return (
                          <div
                            key={vehicle._id}
                            className={`flex items-center justify-between p-3 rounded border cursor-pointer ${
                              vehicle.active === false ? "bg-red-500/10 hover:bg-red-500/15" : "bg-white hover:bg-gray-50"
                            }`}
                            onClick={() => navigate(`/vehicle/${vehicle._id}`)}
                          >
                            <div>
                              <h4 className="font-medium">Vehicle: {vehicle.rego || "No Rego"}</h4>
                              <p className="text-sm text-gray-600">VIN: {vehicle.vin || "N/A"}</p>
                              <p className="text-sm text-gray-600">Active: {vehicle.active !== false ? "Yes" : "No"}</p>
                              {vehicle.activeSubscriptions && vehicle.activeSubscriptions.length > 0 && (
                                <p className="text-sm text-gray-600">Active Subscriptions: {vehicle.activeSubscriptions.length}</p>
                              )}
                            </div>
                            <Button
                              variant="outlined"
                              onClick={(e) => { e.stopPropagation(); navigate(`/vehicle/${vehicle._id}`); }}
                              sx={{ borderColor: COLORS.priorityBlue.hex, color: COLORS.priorityBlue.hex, "&:hover": { borderColor: COLORS.priorityBlue.hex, backgroundColor: `${COLORS.priorityBlue.hex}15` } }}
                            >
                              View Vehicle
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No vehicles yet. Use &quot;Add Vehicle&quot; above to add one.</p>
                  )}
                </div>
              )}

              {sessionStorage.getItem("selectedPortal") === "Administrator" && adminSectionTab === 2 && (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <h3 className="text-lg font-semibold mb-4">Subscriptions</h3>
                  <AdminCustomerSubscriptions
                    vehicles={selectedCustomer.vehicles}
                    onRefresh={() => setRefreshTrigger((t) => t + 1)}
                  />
                </div>
              )}

              {sessionStorage.getItem("selectedPortal") === "Administrator" && adminSectionTab === 3 && (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <h3 className="text-lg font-semibold mb-4">Contract Information</h3>
                  {selectedCustomer.vehicles && selectedCustomer.vehicles.length > 0 ? (
                    <div className="space-y-3">
                      {selectedCustomer.vehicles.map((vehicle) => {
                        if (!vehicle) return null;
                        const hasSubscriptionProduct = vehicle.activeSubscriptions?.length > 0 && vehicle.activeSubscriptions.some((sub) => sub.product?.type === "subscription");
                        const hasContract = vehicleIdsWithContracts.has(String(vehicle._id));
                        if (!hasSubscriptionProduct && !hasContract) return null;
                        const latestContract = getLatestContractForVehicle(contractsByVehicleId, vehicle._id);
                        const contractChip = latestContract
                          ? CONTRACT_STATUS_CHIPS[latestContract.contractStatus] || {
                              label: latestContract.contractStatus,
                              color: "default"
                            }
                          : { label: "No contract", color: "default" };
                        const membershipStatus = subscriptionStatusLabel(vehicle);
                        return (
                          <div key={vehicle._id} className="flex items-center justify-between p-3 bg-white rounded border">
                            <div>
                              <h4 className="font-medium">Vehicle: {vehicle.rego}</h4>
                              <p className="text-sm text-gray-600">VIN: {vehicle.vin}</p>
                              <p className="text-sm text-gray-600">
                                {hasSubscriptionProduct
                                  ? `Active Subscriptions: ${vehicle.activeSubscriptions?.filter((sub) => sub.product?.type === "subscription")?.length ?? 0}`
                                  : hasContract ? "Contract on file" : ""}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <Chip size="small" color={contractChip.color} label={`Contract: ${contractChip.label}`} />
                                <Chip
                                  size="small"
                                  color={membershipStatus === "Active" ? "success" : membershipStatus === "Inactive" ? "warning" : "default"}
                                  label={`Membership: ${membershipStatus}`}
                                />
                              </div>
                            </div>
                            <Button
                              variant="outlined"
                              onClick={() => handleViewContract(vehicle)}
                              sx={{ borderColor: COLORS.priorityBlue.hex, color: COLORS.priorityBlue.hex, "&:hover": { borderColor: COLORS.priorityBlue.hex, backgroundColor: `${COLORS.priorityBlue.hex}15` } }}
                            >
                              View Contracts
                            </Button>
                          </div>
                        );
                      })}
                      {!selectedCustomer.vehicles.some((v) => {
                        if (!v) return false;
                        const hasSub = v.activeSubscriptions?.length > 0 && v.activeSubscriptions.some((sub) => sub.product?.type === "subscription");
                        const hasContract = vehicleIdsWithContracts.has(String(v._id));
                        return hasSub || hasContract;
                      }) && (
                        <p className="text-sm text-gray-500 italic py-2">
                          No active subscriptions or contracts on file.
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No vehicles on file.</p>
                  )}
                </div>
              )}

              {sessionStorage.getItem("selectedPortal") === "Administrator" && adminSectionTab === 4 && (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <RedemptionHistory vehicles={selectedCustomer.vehicles} customerId={params.idCustomer} />
                </div>
              )}

              {sessionStorage.getItem("selectedPortal") === "Administrator" && adminSectionTab === 5 && (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <CustomerCommunications customerId={params.idCustomer} />
                </div>
              )}

              {sessionStorage.getItem("selectedPortal") !== "Administrator" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="flex flex-col gap-6">
                  <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 bg-white">
                    <h2 className="text-lg font-semibold mb-4">Customer Profile</h2>
                    <BasicUserInfo user={selectedCustomer} />
                  </div>
                  </div>
                </div>
                <div className="flex flex-col gap-6">
                {selectedCustomer.vehicles && selectedCustomer.vehicles.some((v) => v?.activeSubscriptions?.length > 0) && (
                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <h3 className="text-lg font-semibold mb-4">Subscriptions</h3>
                    <div className="flex flex-wrap gap-4">
                      {selectedCustomer.vehicles.map((vehicle) =>
                        vehicle?.activeSubscriptions?.length > 0 ? (
                          <div key={vehicle._id} className="flex flex-col gap-4">
                            {vehicle.activeSubscriptions
                              .filter(
                                (subscription) =>
                                  sessionStorage.getItem("selectedPortal") !== "Customer" ||
                                  !isHiddenFromCustomerProduct(subscription.product)
                              )
                              .map((subscription) => (
                              <div
                                key={subscription._id}
                                className="flex flex-col justify-center items-center cursor-pointer border p-2 hover:bg-sky-100 bg-white rounded border-blue-400 border-l-4"
                                onClick={() => navigate(`/subscription/${subscription._id}`)}
                              >
                                <h4 className="text-sm font-medium mb-2">Vehicle: {vehicle.rego} ({vehicle.vin})</h4>
                                {subscription?.qrcode && <img src={subscription.qrcode} alt="Subscription QR" />}
                              </div>
                            ))}
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                )}
                </div>
                <div className="lg:col-span-2 bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <RedemptionHistory vehicles={selectedCustomer.vehicles} />
                </div>
              </div>
              )}

              <ModalPopup
                  showModal={deleteConfirmationIsOpen}
                  handleShowModal={toggleDisplayDeleteConfirm}
                  popupTitle={"Delete Customer"}
                  popupContent={
                    <div>
                      Are you sure you want to delete customer: <strong><em>{selectedCustomer.firstName} {selectedCustomer.lastName}</em></strong>?
                    </div>
                  }
                  cancelButtonText={"Cancel"}
                  confirmButtonText={"Delete"}
                  confirmCallbackAction={handleDeleteCustomerConfirm}
                />
              </>
            )}

            {adminViewTab === 1 && sessionStorage.getItem("selectedPortal") == "Administrator" && (
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <CustomerSubscriptionView customerId={selectedCustomer._id} />
              </div>
            )}
          </div>
        )}

        {/* Admin Contract Viewer Modal */}
        {showContractViewer && selectedVehicleForContract && (
          <AdminContractViewer
            customerId={selectedCustomer._id}
            vehicleId={selectedVehicleForContract._id}
            onClose={handleCloseContractViewer}
          />
        )}

        {/* Product Selection Modal */}
        <ModalPopup
          showModal={showProductSelectModal}
          handleShowModal={() => {
            setShowProductSelectModal(false);
            setSelectedProductId(null);
            setSelectedRrp("");
          }}
          popupTitle="Select Product for Signup Link"
          popupContent={
            <div className="flex flex-col gap-4 p-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Customer:</strong> {selectedCustomer?.firstName} {selectedCustomer?.lastName}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Email:</strong> {selectedCustomer?.email}
              </p>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">
                  Select Product <span className="text-red-500">*</span>
                </label>
                <select
                  className="border rounded px-3 py-2 w-full"
                  value={selectedProductId || ""}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value);
                    // Set default RRP when product is selected
                    if (e.target.value) {
                      const product = availableProducts.find((p) => p._id === e.target.value);
                      if (product) {
                        // RRP is already in dollars, use it directly
                        const defaultRrp = product.rrp || product.price || 0;
                        setSelectedRrp(defaultRrp.toFixed(2));
                      }
                    } else {
                      setSelectedRrp("");
                    }
                  }}
                >
                  <option value="">-- Select Product --</option>
                  {availableProducts.map((product) => (
                    <option key={product._id} value={product._id}>
                      {product.name}
                      {" - $"}
                      {(product.price || product.rrp || 0).toFixed(2)}
                      {product.interval && ` (${product.interval === "month" ? "Monthly" : product.interval === "year" ? "Yearly" : product.interval})`}
                      {product.type && !product.interval && ` (${product.type})`}
                    </option>
                  ))}
                </select>
              </div>

              {selectedProductId && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-semibold mb-2">
                      RRP Price (in dollars) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      className="border rounded px-3 py-2 w-full"
                      placeholder="Enter RRP (e.g., 99.99)"
                      value={selectedRrp}
                      onChange={(e) => setSelectedRrp(e.target.value)}
                      min="0"
                      step="0.01"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Default RRP is pre-filled from product, but you can modify it if needed.
                    </p>
                  </div>

                  <div className="p-3 bg-blue-50 rounded border border-blue-200">
                    <p className="text-xs text-blue-700">
                      <strong>Note:</strong> This signup link will be linked to P1 Internal Partner by default.
                    </p>
                  </div>
                </>
              )}

              <div className="flex gap-2 justify-end mt-4">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowProductSelectModal(false);
                    setSelectedProductId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleProductSelected}
                  disabled={!selectedProductId || !selectedRrp || generatingLink}
                  sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                >
                  {generatingLink ? "Generating..." : "Generate Link"}
                </Button>
              </div>
            </div>
          }
          cancelButtonText=""
          confirmButtonText=""
          confirmCallbackAction=""
        />

        {/* Signup Link QR Code Modal */}
        <ModalPopup
          showModal={showSignupLinkModal}
          handleShowModal={() => {
            setShowSignupLinkModal(false);
            setSignupLinkData(null);
          }}
          popupTitle="Signup Link & QR Code"
          popupContent={
            signupLinkData ? (
              <div className="flex flex-col gap-4 p-4">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Customer:</strong> {signupLinkData.customerName}
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Email:</strong> {signupLinkData.customerEmail}
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Product:</strong> {signupLinkData.productName}
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>RRP Price:</strong> ${signupLinkData.rrp ? signupLinkData.rrp.toFixed(2) : "0.00"}
                  </p>
                  <p className="text-xs text-orange-600 mt-4 p-2 bg-orange-50 rounded">
                    <strong>Important:</strong> When the customer completes their purchase through this link, it will
                    automatically be linked to their existing account ({signupLinkData.customerEmail}).
                  </p>
                </div>

                <div className="flex flex-col md:flex-row gap-6 items-center justify-center">
                  {/* QR Code */}
                  <div className="flex flex-col items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold">QR Code</h4>
                    <QRCodeCanvas value={signupLinkData.signupLink} size={256} level="H" />
                  </div>

                  {/* Signup Link */}
                  <div className="flex flex-col gap-3 flex-1">
                    <h4 className="font-semibold">Signup Link</h4>
                    <div className="p-3 bg-gray-50 rounded border">
                      <p className="text-xs text-gray-600 break-all mb-2">{signupLinkData.signupLink}</p>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          navigator.clipboard.writeText(signupLinkData.signupLink);
                          alert("Link copied to clipboard!");
                        }}
                        size="small"
                        sx={{ width: "100%", mt: 1 }}
                      >
                        Copy Link
                      </Button>
                    </div>

                    <a
                      href={signupLinkData.signupLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm text-center"
                    >
                      Open Link in New Tab
                    </a>
                  </div>
                </div>

                {/* Send via Email and SMS buttons */}
                <div className="flex flex-col gap-3 mt-4 pt-4 border-t">
                  <h4 className="font-semibold text-center">Send Signup Link</h4>
                  <div className="flex gap-3">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSendViaEmail}
                      disabled={sendingLink.email || !signupLinkData.customerEmail}
                      sx={{
                        flex: 1,
                        backgroundColor: COLORS.priorityBlue.hex,
                        "&:hover": {
                          backgroundColor: COLORS.priorityBlue.hex,
                          opacity: 0.9
                        }
                      }}
                    >
                      {sendingLink.email ? "Sending..." : "Send via Email"}
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleSendViaSMS}
                      disabled={sendingLink.sms || !signupLinkData.customerPhone}
                      sx={{
                        flex: 1,
                        backgroundColor: "#4CAF50",
                        "&:hover": {
                          backgroundColor: "#45a049"
                        },
                        "&:disabled": {
                          backgroundColor: "#cccccc"
                        }
                      }}
                    >
                      {sendingLink.sms ? "Sending..." : "Send via SMS"}
                    </Button>
                  </div>
                  {!signupLinkData.customerEmail && (
                    <p className="text-xs text-orange-600 text-center">Email not available for this customer</p>
                  )}
                  {!signupLinkData.customerPhone && (
                    <p className="text-xs text-orange-600 text-center">Phone number not available for this customer</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-4">Loading...</div>
            )
          }
          cancelButtonText="Close"
          confirmButtonText=""
          confirmCallbackAction=""
        />
      </div>
    )
  );
};

export default CustomerSingle;
